import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useToastStore } from '../../../stores/toastStore';
import {
  AdminQueryKeys,
  FORCE_DI_SENTINEL,
  getAdminTenantDetail,
  setTenantClassification,
  setTenantOcrLimits,
  setTenantOcrRouting,
  setTenantPermissions,
  type TenantAdminDetailResponse,
} from '../api';
import { AiUsageSection } from '../components/AiUsageSection';
import { PlanBadge } from '../components/PlanBadge';
import { TenantRoiCostSection } from '../components/TenantRoiCostSection';
import { TenantStatusBadge } from '../components/TenantStatusBadge';
import { useOcrProviders } from '../hooks/useOcrProviders';

// ---- helpers -----------------------------------------------------------------

function nToStr(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function strToN(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// ---- sub-component -----------------------------------------------------------

function OcrLimitsPanel({ tenantId, detail }: { tenantId: string; detail: TenantAdminDetailResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);

  const [readVal, setReadVal] = useState<string>(nToStr(detail.ocrReadLimitOverride));
  const [layoutVal, setLayoutVal] = useState<string>(nToStr(detail.ocrLayoutLimitOverride));
  const [documentVal, setDocumentVal] = useState<string>(nToStr(detail.ocrDocumentLimitOverride));

  const mutation = useMutation({
    mutationFn: () =>
      setTenantOcrLimits(tenantId, {
        readCallsPerMonth: strToN(readVal),
        layoutCallsPerMonth: strToN(layoutVal),
        documentCallsPerMonth: strToN(documentVal),
      }),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Límites OCR guardados.' });
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.tenantDetail(tenantId) });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error desconocido.';
      pushToast({ tone: 'error', title: 'No se pudieron guardar los límites OCR', description: msg });
    },
  });

  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-slate-900">Límites OCR</h2>
      <p className="mt-1 text-xs text-slate-500">
        Vacío = hereda el límite del plan. Un valor sobreescribe el plan para este tenant.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="ocr-read">prebuilt-read / mes</label>
          <input
            id="ocr-read"
            type="number"
            min={0}
            className="input"
            placeholder="Heredar del plan"
            value={readVal}
            onChange={(e) => setReadVal(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="label" htmlFor="ocr-layout">prebuilt-layout / mes</label>
          <input
            id="ocr-layout"
            type="number"
            min={0}
            className="input"
            placeholder="Heredar del plan"
            value={layoutVal}
            onChange={(e) => setLayoutVal(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="label" htmlFor="ocr-document">prebuilt-document / mes</label>
          <input
            id="ocr-document"
            type="number"
            min={0}
            className="input"
            placeholder="Heredar del plan"
            value={documentVal}
            onChange={(e) => setDocumentVal(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
      </div>
      {mutation.isError ? (
        <p className="mt-2 text-xs text-rose-700">
          {mutation.error instanceof Error ? mutation.error.message : 'Error desconocido.'}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar límites OCR'}
        </button>
      </div>
    </div>
  );
}

function OcrSourcePermissionPanel({ tenantId, detail }: { tenantId: string; detail: TenantAdminDetailResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);

  const mutation = useMutation({
    mutationFn: (canEdit: boolean) => setTenantPermissions(tenantId, { canEditOcrSource: canEdit }),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Permiso actualizado.' });
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.tenantDetail(tenantId) });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error desconocido.';
      pushToast({ tone: 'error', title: 'No se pudo actualizar el permiso', description: msg });
    },
  });

  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-slate-900">Permisos del tenant</h2>
      <label className="mt-3 flex items-start gap-3">
        <input
          type="checkbox"
          checked={detail.canEditOcrSource}
          onChange={(e) => mutation.mutate(e.target.checked)}
          disabled={mutation.isPending}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
        />
        <span className="text-sm text-slate-700">
          <span className="font-medium text-slate-900">Permitir elegir la fuente OCR</span>
          <span className="mt-0.5 block text-xs text-slate-500">
            Si está activo, los usuarios de este tenant pueden cambiar el motor OCR (proveedor / modelo /
            capacidades) de sus tipos de documento. Si no, la fuente OCR la gestionas solo tú.
          </span>
        </span>
      </label>
    </div>
  );
}

function OcrRoutingPanel({ tenantId, detail }: { tenantId: string; detail: TenantAdminDetailResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);
  const { data: ocrProviders } = useOcrProviders();

  // tri-state auto-routing is encoded as '' (inherit) | 'true' | 'false'.
  const [discovery, setDiscovery] = useState<string>(detail.discoveryOcrProviderIdOverride ?? '');
  const [autoRouting, setAutoRouting] = useState<string>(
    detail.processingAutoRoutingOverride === null || detail.processingAutoRoutingOverride === undefined
      ? ''
      : String(detail.processingAutoRoutingOverride),
  );
  const [complex, setComplex] = useState<string>(detail.complexDocOcrProviderIdOverride ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      setTenantOcrRouting(tenantId, {
        discoveryOcrProviderIdOverride: discovery === '' ? null : discovery,
        processingAutoRoutingOverride: autoRouting === '' ? null : autoRouting === 'true',
        complexDocOcrProviderIdOverride: complex === '' ? null : complex,
      }),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Motor OCR guardado.' });
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.tenantDetail(tenantId) });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error desconocido.';
      pushToast({ tone: 'error', title: 'No se pudo guardar el motor OCR', description: msg });
    },
  });

  const providers = ocrProviders ?? [];

  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-slate-900">Motor OCR (por tenant)</h2>
      <p className="mt-1 text-xs text-slate-500">
        Sobreescribe, solo para este tenant, qué motor OCR se usa. Vacío = hereda el plan / el default de la
        plataforma.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="rt-discovery">Discovery</label>
          <select
            id="rt-discovery"
            className="input"
            value={discovery}
            onChange={(e) => setDiscovery(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">Heredar (default plataforma)</option>
            <option value={FORCE_DI_SENTINEL}>Forzar Document Intelligence</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.displayName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="rt-auto">Enrutado automático (procesar)</label>
          <select
            id="rt-auto"
            className="input"
            value={autoRouting}
            onChange={(e) => setAutoRouting(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">Heredar del plan</option>
            <option value="true">Activado</option>
            <option value="false">Desactivado</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="rt-complex">Proveedor docs. complejos</label>
          <select
            id="rt-complex"
            className="input"
            value={complex}
            onChange={(e) => setComplex(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">Heredar del plan</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.displayName}</option>
            ))}
          </select>
        </div>
      </div>
      {mutation.isError ? (
        <p className="mt-2 text-xs text-rose-700">
          {mutation.error instanceof Error ? mutation.error.message : 'Error desconocido.'}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar motor OCR'}
        </button>
      </div>
    </div>
  );
}

function ClassificationOverridePanel({ tenantId, detail }: { tenantId: string; detail: TenantAdminDetailResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);

  // tri-state: '' (inherit the plan) | 'true' (force on) | 'false' (force off).
  const [override, setOverride] = useState<string>(
    detail.autoClassificationOverride === null || detail.autoClassificationOverride === undefined
      ? ''
      : String(detail.autoClassificationOverride),
  );

  const mutation = useMutation({
    mutationFn: () =>
      setTenantClassification(tenantId, {
        autoClassificationOverride: override === '' ? null : override === 'true',
      }),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Clasificación automática guardada.' });
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.tenantDetail(tenantId) });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error desconocido.';
      pushToast({ tone: 'error', title: 'No se pudo guardar la clasificación automática', description: msg });
    },
  });

  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-slate-900">Clasificación automática (por tenant)</h2>
      <p className="mt-1 text-xs text-slate-500">
        Sobreescribe, solo para este tenant, si puede subir documentos en modo «Automático». Heredar =
        usa el valor del plan.
      </p>
      <div className="mt-4 max-w-xs">
        <label className="label" htmlFor="cls-override">Modo</label>
        <select
          id="cls-override"
          className="input"
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          disabled={mutation.isPending}
        >
          <option value="">Heredar del plan</option>
          <option value="true">Activada</option>
          <option value="false">Desactivada</option>
        </select>
      </div>
      {mutation.isError ? (
        <p className="mt-2 text-xs text-rose-700">
          {mutation.error instanceof Error ? mutation.error.message : 'Error desconocido.'}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar clasificación'}
        </button>
      </div>
    </div>
  );
}

// ---- page --------------------------------------------------------------------

export function TenantDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: AdminQueryKeys.tenantDetail(id ?? ''),
    queryFn: ({ signal }) => getAdminTenantDetail(id ?? '', signal),
    enabled: id !== undefined && id !== '',
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );
  }

  if (isError || data === undefined) {
    return (
      <div className="card border-rose-200 bg-rose-50 p-6 text-rose-800">
        <p className="font-medium">No se pudo cargar el tenant.</p>
        <button type="button" className="btn-secondary mt-3" onClick={() => void navigate('/admin/tenants')}>
          Volver a la lista
        </button>
      </div>
    );
  }

  const { summary } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => void navigate('/admin/tenants')} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Volver">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <h1 className="text-2xl font-semibold text-slate-900">{summary.name}</h1>
        <TenantStatusBadge status={summary.status} />
        <PlanBadge plan={summary.licenseType} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <dl className="card p-5">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Tenant ID</dt>
          <dd className="mt-1 font-mono text-sm text-slate-900">{summary.tenantId}</dd>
        </dl>
        <dl className="card p-5">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Documentos este mes</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
            {summary.monthlyDocumentCount.toLocaleString('es-ES')}
            {summary.monthlyDocumentLimit !== null ? (
              <span className="text-base font-normal text-slate-400"> / {summary.monthlyDocumentLimit.toLocaleString('es-ES')}</span>
            ) : null}
          </dd>
        </dl>
        <dl className="card p-5">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Expiración de licencia</dt>
          <dd className="mt-1 text-sm text-slate-900">
            {new Date(summary.licenseExpiresAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </dd>
        </dl>
      </div>

      <OcrSourcePermissionPanel tenantId={summary.tenantId} detail={data} />

      <OcrLimitsPanel tenantId={summary.tenantId} detail={data} />

      <OcrRoutingPanel tenantId={summary.tenantId} detail={data} />

      <ClassificationOverridePanel tenantId={summary.tenantId} detail={data} />

      <TenantRoiCostSection tenantId={summary.tenantId} />

      <AiUsageSection tenantId={summary.tenantId} />
    </div>
  );
}
